import React, { useContext, useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { wizardProps, WizardContext } from 'wmic-pe-portals-custom-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MockUpUtil } from '@xengage/gw-portals-util-js';
import { useDocumentTitle, useWizardModals, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { WMICUnderwritingIssues } from 'wmic-pe-components-platform-react';
import { WMICPUPPrintableAgreement } from 'wmic-pe-capability-gateway-common-pup-react';
import { DocumentService } from 'wmic-pe-capability-gateway-policycommon';
import { CONSTANTS, WMICLogger, WMICVariousUtil, parseErrorMessage } from 'wmic-pe-portals-utils-js';
import { WMICDocuments } from 'wmic-pe-capability-gateway-common-react';
import { WMICErrorHandler, WMICQuoteUtil } from 'wmic-pe-capability-quoteandbind-common-react';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';

import messages from './WMICPUPQuote.messages';
import styles from './WMICPUPQuote.module.scss';
import metadata from './WMICPUPQuote.metadata.json5';
import { TranslatorContext } from '@jutro/legacy/locale';

function WMICPUPQuote(props) {
    const { authHeader } = useAuthentication();
    const { isComponentValid, onValidate, initialValidation, registerComponentValidation } = useValidation('QuotePage');
    const history = useHistory();
    const viewModelService = useContext(ViewModelServiceContext);
    const { isSkipping } = useContext(WizardContext);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { wizardData: submissionVM, updateWizardData, markStepSubmitted } = props;
    const { setWizardLoading } = useWizardModals();
    const translator = useContext(TranslatorContext);
    const [userDocuments, setUserDocuments] = useState([]);

    useDocumentTitle(translator(messages.pupQuoteTitle), submissionVM);

    useEffect(() => {
        // inject mock contactPhone to prevent wizardData.subtreeValid is invalid
        submissionVM.value = MockUpUtil.setMockData(submissionVM.value, 'quote.ho', 'bindData.contactPhone');

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDocuments = useCallback(async () => {
        const documents = await DocumentService.getDocumentsForJobNumber(submissionVM.quoteID.value, authHeader);
        if (userDocuments.length !== documents.length) {
            setUserDocuments(documents);
        }
    }, [authHeader, submissionVM, userDocuments]);

    const validateSubmission = useCallback(async () => {
        const newSubmissionVM = viewModelService.clone(submissionVM);
        _.unset(newSubmissionVM.value, 'bindData');
        const result = await LoadSaveService.validateSubmissionForIssue(newSubmissionVM.value, authHeader);
        _.extend(submissionVM.value, result);
        updateWizardData(submissionVM);
        markStepSubmitted(); // the step needs to be marked as submitted for the errors associated with this step to show up
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [LoadSaveService, authHeader, submissionVM, viewModelService, setWizardLoading, markStepSubmitted]);

    const underwritingIssues = _.get(submissionVM, 'errorsAndWarnings.underwritingIssues.value', []);

    const hasQuoteBlockingUWIssues = useCallback(
        () => WMICQuoteUtil.hasQuoteBlockingUWIssues(submissionVM),
        [submissionVM]
    );

    const isTransactionQuoted = useCallback(
        () => submissionVM?.baseData?.periodStatus?.value?.code === CONSTANTS.QUOTED,
        [submissionVM]
    );

    const checkComponentValidation = useCallback(
        () =>
            !(
                WMICQuoteUtil.isBlockingUWIssuePresent(submissionVM) ||
                WMICQuoteUtil.hasValidationErrors(submissionVM) ||
                hasQuoteBlockingUWIssues() ||
                !isTransactionQuoted()
            ),
        [submissionVM, hasQuoteBlockingUWIssues, isTransactionQuoted]
    );

    useEffect(() => {
        registerComponentValidation(checkComponentValidation);
    }, [registerComponentValidation, checkComponentValidation]);

    const loadAdditionalInformation = useCallback(async () => {
        setWizardLoading(!isSkipping, translator(commonMessages.loading));
        await Promise.all([validateSubmission(), fetchDocuments()])
            .catch(error => {
                const jobID = submissionVM.quoteID.value;
                WMICLogger.error('Unable to load additional information', error);
                WMICErrorHandler.processAsModal(parseErrorMessage(error)).catch(() =>
                    history.push(`/quotes/${jobID}/summary`)
                );
            })
            .finally(() => setWizardLoading(false));
    }, [
        isSkipping,
        translator,
        validateSubmission,
        fetchDocuments,
        setWizardLoading,
        submissionVM.quoteID.value,
        history,
    ]);

    useEffect(() => {
        loadAdditionalInformation();
    }, [loadAdditionalInformation]);

    const onNext = useCallback(async () => {
        if (!isComponentValid) {
            WMICVariousUtil.scrollToTop();
            return false;
        }
        return submissionVM;
    }, [isComponentValid, submissionVM]);

    const isQuoteInfoAvailable = !hasQuoteBlockingUWIssues() && isTransactionQuoted();
    const isUploadDocumentsSectionVisible = !hasQuoteBlockingUWIssues() && isTransactionQuoted();

    const overrideProps = {
        wmicQuoteAlertMessage: {
            jobVM: submissionVM,
        },
        quotePagePremiumCosts: {
            premium: _.get(submissionVM, 'quoteData.offeredQuotes.value[0].premium', {}),
            jobVM: submissionVM,
            visible: isQuoteInfoAvailable,
        },
        quotePageUWIssues: {
            underwritingIssues,
            quoteID: _.get(submissionVM, 'quoteID.value'),
            authHeader,
            jobVM: submissionVM,
        },
        quotePageSummary: {
            jobVM: submissionVM,
            lobData: _.get(submissionVM, 'lobData.personalUmbrella', {}),
            visible: isQuoteInfoAvailable,
        },
        quotePageUploadDocuments: {
            documents: userDocuments,
            updateDocuments: setUserDocuments,
            value: {
                documentDTOs: userDocuments,
                canUploadDocument: true,
                jobNumber: _.get(submissionVM, 'quoteID.value'),
            },
            visible: isUploadDocumentsSectionVisible,
        },
        quotePagePrintableAgreement: {
            jobVM: submissionVM,
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        componentMap: {
            WMICUnderwritingIssues,
            WMICDocuments,
            WMICPUPPrintableAgreement,
        },
    };

    return (
        <WMICWizardSubmissionPage
            disableNext={!isComponentValid}
            onNext={onNext}
            skipWhen={initialValidation}
            cancelLabel={translator(commonMessages.saveAndExit)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                componentMap={resolvers.componentMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICPUPQuote.propTypes = wizardProps;
export default WMICPUPQuote;
